// StepContext.tsx

import { InvestingPersonEnum, RetirementAccountEnum } from 'interfaces'
import React, { useState } from 'react'

interface AccreditationItem {
  statement: string
  agreed: string | boolean // This can also be `boolean` if the "true" and "false" strings are intended to be boolean values.
}

interface AgreementItem {
  statement: string
  agreed: string | boolean // Same note as above regarding boolean type.
}

interface BasicInfo {
  firstName: string
  lastName: string
  middleName: string
  suffix: string
}

interface AddressInfo {
  country: string
  state: string
  Street1: string
  Street2: string
  city: string
  postalCode: string
}

interface Agreement {
  statement: string
  agreed: string
}

interface SelfAccreditation2 {
  agreement1: Agreement
  agreement2: Agreement
  agreement3: Agreement
  agreement4: Agreement
  agreement5: Agreement
  agreement6: Agreement
  agreement7: Agreement
  agreement8: Agreement
}

interface InvestingProfile {
  itemId: string
  option: string
  amount: number
  totalShares: number
  accreditedInvestor: string
  selfAccreditation2: SelfAccreditation2
}

interface Secondary {
  email: string
  addressInfo: AddressInfo
  basicInfo: BasicInfo
  personalInfo: PersonalInfo
  financialInfo: FinancialInfo
}

interface MailingAddress {
  country: string
  street1: string
  street2: string
  state: string
  city: string
  postalCode: string
}

interface EntityInfo {
  countryOfFormation?: string
  regionOfFormation?: string
  type?: string
  companyName?: string
  phoneNumber?: string
  email?: string
  establishedOn?: string
  legalStructure?: string
  authorized?: string
  addressInfo?: AddressInfo // Assuming AddressInfo is a defined type
  name?: string
  ein?: string
  nameOfCustodian?: string
  emailOfIraContact?: string
}

interface PersonalInfo {
  phoneNumber: string
  birthday: string
}

interface FinancialInfo {
  ssn: string
}

interface SelfAccreditation1 {
  accreditationA: AccreditationItem
  accreditationB: AccreditationItem
  accreditationC: AccreditationItem
}

export interface FormInputs {
  email: string
  basicInfo: BasicInfo
  _id?: string // Making this optional since it might not always be provided in the form
  onboardingId: string
  regulation: string
  addressInfo: AddressInfo
  investingProfile: {
    itemId: string
    option: InvestingPersonEnum // Since UserProfile uses string, making it compatible
    amount: number
    totalShares: number
    retirementAccountType: RetirementAccountEnum
    accreditedInvestor: string
    selfAccreditation1?: SelfAccreditation1 // Making this optional
    selfAccreditation2: {
      agreement1: AgreementItem
      agreement2: AgreementItem
      agreement3: AgreementItem
      agreement4: AgreementItem
      agreement5: AgreementItem
      agreement6: AgreementItem
      agreement7: AgreementItem
      agreement8: AgreementItem
    }
  }
  personalInfo: PersonalInfo
  financialInfo: FinancialInfo
  secondary?: Secondary
  entityDetails?: EntityInfo
  ipAddress?: string
}

export interface FormContextInterface {
  email: string
  setEmail: React.Dispatch<React.SetStateAction<string>>
}

export const FormContext = React.createContext<FormContextInterface>({
  email: '',
  setEmail: () => {},
})

export const FormContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [email, setEmail] = useState('')

  return <FormContext.Provider value={{ email, setEmail }}>{children}</FormContext.Provider>
}
